import type { ReviewResearchMode, SessionUser } from "../shared/types.js";
import type { LeadFocus, TargetProfile } from "../shared/targetProfiles.js";

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export type JsonObject = { [key: string]: JsonValue };

export type Lead = {
  id: number;
  placeId?: string;
  name: string;
  address: string | null;
  phone: string | null;
  internationalPhone?: string | null;
  website: string | null;
  googleUrl?: string | null;
  latitude: string | null;
  longitude: string | null;
  rating: string | null;
  reviewCount: number;
  priceLevel?: number | null;
  businessStatus?: string | null;
  cuisine: string | null;
  companyType?: "operational" | "consultancy" | "unknown";
  employeeEstimate?: number | null;
  manualOverrides?: JsonObject | null;
  employeeBand?: "under_10" | "10_49" | "50_249" | "250_500" | "over_500" | "unknown";
  targetFit?: "target" | "review" | "excluded";
  opportunityScore?: number;
  opportunityThemes?: Array<"process_improvement" | "automation" | "growth" | "restructuring" | "cost_reduction"> | null;
  opportunitySignals?: JsonObject | null;
  opportunityReason?: string | null;
  serviceModel?: "sit_down" | "takeaway_order" | "hybrid" | "unclear";
  leadQuality?: LeadQuality | null;
  leadQualityReason?: string | null;
  categories?: string[] | null;
  hasWebPresence: boolean;
  webPresenceType: string;
  hasReservation: boolean;
  reservationProvider: string | null;
  reservationConfidence: string;
  reservationUrl?: string | null;
  hasDelivery: boolean;
  deliveryProvider: string | null;
  deliveryConfidence: string;
  deliveryUrl?: string | null;
  hubspotCompanyId?: string | null;
  hubspotContactId?: string | null;
  priority: number;
  score: number;
  email: string | null;
  emailQuality?: string;
  hasUsableEmail?: boolean;
  contactStatus?: "complete" | "missing_email" | "missing_director" | "missing_both";
  outreachOptOut?: boolean;
  outreachStage?: OutreachStage | null;
  impressumUrl: string | null;
  legalName: string | null;
  managingDirector?: string | null;
  taxId?: string | null;
  menuUrl: string | null;
  socialUrls?: string[] | null;
  openingHours: JsonObject | null;
  evidence?: JsonObject | null;
  reviewStatus: string;
  reviewNotes: string | null;
  acquisitionType?: AcquisitionType | null;
  acquisitionPriority?: number | null;
  lastDiscoveredAt?: string | null;
  lastCrawledAt?: string | null;
  lastClassifiedAt?: string | null;
  crawlStatus?: "pending" | "success" | "partial" | "failed" | "blocked" | "no_website";
  lastCrawlError?: string | null;
  lastOutreachAt?: string | null;
  followUpDueAt?: string | null;
  outreachSendCount?: number | null;
  outreachPlan?: OutreachPlan | null;
  outreachTemplateId?: number | null;
};

export type AcquisitionType = "unassigned" | "automation" | "distress" | "transformation" | "none" | "blocked";
export type ReviewStage = "new" | "reviewed" | "blocked";
export type OutreachStage = "open" | "acquisition_email_sent" | "called" | "not_interested" | "lead";
export type OutreachPlan = "unassigned" | "email_only" | "phone_manual" | "email_then_phone";
export type LeadQuality = "good_fit" | "review" | "chain" | "hotel" | "non_company" | "unclear";
export type MailAttachment = { name: string; contentType: string; contentBase64: string };

export type OutreachTemplate = {
  id: number;
  name: string;
  track: "a" | "b";
  channel: "email";
  subject: string;
  bodyTemplate: string;
  variables: string[] | null;
  active: boolean;
  updatedAt: string;
};

export type OutreachTemplateForm = {
  id: number | null;
  name: string;
  track: "a" | "b";
  subject: string;
  bodyTemplate: string;
  active: boolean;
};
export type SettingsTab = "integrations" | "mail" | "outreach" | "crawler";

export const emptyOutreachTemplateForm: OutreachTemplateForm = {
  id: null,
  name: "",
  track: "a",
  subject: "Kurzer Check für {{companyName}}",
  bodyTemplate: "Hallo {{companyName}} Team,\n\nwir haben uns Ihre Online-Präsenz kurz angesehen.\n\n{{signature}}\n\nWenn Sie zu diesem Thema keine weitere Nachricht von uns möchten, genügt ein Klick hier: {{unsubscribeUrl}}",
  active: true
};

export type OutreachSend = {
  id: number;
  companyId: number;
  templateId: number | null;
  track: "a" | "b";
  status: string;
  recipientEmail: string | null;
  providerMessageId: string | null;
  subject: string | null;
  error: string | null;
  sentAt: string | null;
  attemptCount?: number;
  nextAttemptAt?: string | null;
  lastAttemptAt?: string | null;
  createdAt: string;
};

export type MailConnectionView = {
  connected: boolean;
  email: string | null;
  displayName: string | null;
  expiresAt: string | null;
  scopes: string | null;
  lastError: string | null;
  source: "user" | "fallback" | "missing";
  fallbackEmail: string | null;
  userEmail: string | null;
  userDisplayName: string | null;
  provider?: "microsoft_graph" | "ionos_smtp";
  mode?: string;
};

export type OutreachCampaign = {
  id: number;
  name: string;
  track: "a" | "b";
  status: string;
  companyIds: number[];
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  replyCount?: number;
  bounceCount?: number;
  optOutCount?: number;
  replyRate?: number;
  bounceRate?: number;
  guard?: OutreachGuard;
  createdAt: string;
  sentAt?: string | null;
};

export type OutreachGuard = {
  eligibleIds: number[];
  blocked: Array<{ companyId: number; name: string; reason: string }>;
  warnings: Array<{ companyId: number; name: string; reason: string }>;
};

export type OutreachPreviewRow = {
  companyId: number;
  name: string;
  email: string | null;
  emailQuality?: string;
  managingDirector?: string | null;
  leadQuality?: LeadQuality | null;
  acquisitionType?: AcquisitionType | null;
  acquisitionPriority?: number | null;
  subject: string;
  text: string;
};

export type LeadReviewFeedback = {
  id: number;
  companyId: number;
  feedbackType: string;
  fieldName: string | null;
  notes: string | null;
  createdAt: string;
};

export type PitchSummary = {
  text: string;
  source: "openai" | "rule-based";
  model?: string | null;
  createdAt?: string | null;
};

export type CrmExport = {
  id: number;
  provider: string;
  remoteId: string | null;
  status: string;
  error: string | null;
  exportedAt: string;
  response?: JsonObject | null;
};

export type OutreachSuppression = {
  id: number;
  email: string | null;
  domain: string | null;
  reason: string;
  source: string;
  createdAt: string;
};

export type Job = {
  id: number;
  city: string;
  radiusMeters: number;
  status: string;
  totalFound: number;
  totalCrawled: number;
  totalClassified: number;
  totalFailed: number;
  error: string | null;
  createdAt: string;
  filters?: JsonObject | null;
};

export type CrawlJobReport = {
  job: Job;
  report: {
    jobId: number;
    found: number;
    crawled: number;
    failed: number;
    timeout: number;
    noWeb: number;
    noImpressum: number;
    noEmail: number;
    priorityBreakdown: Record<string, number>;
    reservationConfidence: Record<string, number>;
    deliveryConfidence: Record<string, number>;
    googleDetailsCalls: number;
  };
  pageStats: {
    pages: number;
    companies: number;
    httpFailed: number;
    snapshots: number;
  };
  classifierStats: Array<{
    type: string;
    confidence: string;
    total: number;
  }>;
  changes: LeadChange[];
  quality: {
    crawlFailureRate: number;
    snapshotCoverage: number;
    discoveryCoverage: DiscoveryCoverage | null;
  };
};

export type DiscoveryCoverage = {
  mode: "cache" | "standard" | "deep";
  cacheHit: boolean;
  nearbyCoveragePointsPlanned: number;
  nearbyCoveragePointsExecuted: number;
  textKeywordsPlanned: number;
  textKeywordsExecuted: number;
  stoppedEarlyAtCandidateLimit: boolean;
  rawUniqueCandidates: number;
  inRadiusCandidates: number;
  rankedCandidates: number;
  selectedCandidates: number;
  candidateLimit: number;
};

export type CrawlRegion = {
  id: number;
  name: string;
  city: string;
  radiusMeters: number;
  keyword: string | null;
  targetProfile: TargetProfile;
  leadFocus: LeadFocus;
  active: boolean;
  forceRefresh: boolean;
  intervalDays: number;
  lastQueuedAt: string | null;
  nextRunAt: string | null;
};

export type DedupeGroup = {
  key: string;
  reason: string;
  companies: Lead[];
};

export type LeadList = {
  id: number;
  name: string;
  filters: JsonObject;
  createdBy: number | null;
  updatedAt: string;
};

export type AdminUser = {
  id: number;
  email: string;
  name: string;
  role: SessionUser["role"];
  active: boolean;
  createdAt: string;
};

export type AuditLog = {
  id: number;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: number | null;
  metadata: JsonObject | null;
  createdAt: string;
};

export type CrawlPage = {
  id: number;
  url: string;
  title: string | null;
  httpStatus: number | null;
  snapshotPath: string | null;
  extracted: JsonObject | null;
  crawledAt: string;
};

export type LeadEvent = {
  id: number;
  source: string;
  eventType: string;
  outcome: string | null;
  notes: string | null;
  externalId: string | null;
  payload: JsonObject | null;
  createdAt: string;
};

export type LeadPainOverviewItem = {
  id: number;
  source: string;
  score: number;
  level: string | null;
  labels: string[];
  solutions: string[];
  sourceUrl: string | null;
  notes: string | null;
  reviewExcerpt: string | null;
  overallRating: number | null;
  totalReviewCount: number | null;
  createdAt: string;
};

export type LeadPainOverview = {
  leadId: number;
  count: number;
  score: number;
  labels: string[];
  solutions: string[];
  consultingTracks: string[];
  sources: string[];
  latestAt: string | null;
  items: LeadPainOverviewItem[];
};

export type PainOverviewResponse = { mode: ReviewResearchMode; leads: LeadPainOverview[] };

export type LeadChange = {
  id: number;
  companyId: number;
  jobId: number | null;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changeType: string;
  createdAt: string;
};

export type Tab = "dashboard" | "leads" | "review" | "outreach" | "crawl" | "settings" | "users" | "audit";

export const emptyLeadFilters = {
  search: "",
  city: "",
  cuisine: "",
  serviceModel: "",
  leadQuality: "",
  companyType: "",
  targetProfile: "",
  employeeBand: "",
  targetFit: "",
  opportunityTheme: "",
  painSignal: "",
  painConfidence: "",
  painSource: "",
  minPainScore: "",
  minOpportunityScore: "",
  crawlStatus: "",
  crawlIssue: false,
  priority: "",
  reviewStatus: "",
  reviewStage: "",
  acquisitionType: "",
  acquisitionPriority: "",
  outreachStage: "",
  minReviews: "",
  reservationProvider: "",
  deliveryProvider: "",
  reservationConfidence: "",
  deliveryConfidence: "",
  offersSeating: false,
  offersTakeaway: false,
  offersBoth: false,
  hasReservationCompetitor: false,
  hasDeliveryCompetitor: false,
  hasAnyCompetitor: false,
  noOnlineSystems: false,
  noReservation: false,
  noDelivery: false,
  noWebPresence: false,
  hasPhone: false,
  hasEmail: false,
  noEmail: false,
  hasUsableEmail: false,
  noUsableEmail: false,
  emailQuality: "",
  contactStatus: "",
  hasManagingDirector: false,
  noManagingDirector: false,
  contactComplete: false,
  needsContactResearch: false,
  outreachReady: false,
  lowConfidence: false,
  uncertain: false
};

export const reviewStageOptions: Array<{ value: ReviewStage; label: string; description: string }> = [
  { value: "new", label: "Neu", description: "Rohfund, noch nicht menschlich freigegeben." },
  { value: "reviewed", label: "Gereviewed", description: "Menschlich geprüft und in eine Akquise-Kategorie eingeordnet." },
  { value: "blocked", label: "Gesperrt", description: "Nicht kontaktieren, nicht exportieren, nicht in Akquise-Listen." }
];

export const acquisitionTypeOptions: Array<{ value: AcquisitionType; label: string; description: string }> = [
  { value: "unassigned", label: "Offen", description: "Noch keine Akquise-Entscheidung getroffen." },
  { value: "automation", label: "Mobile Zeiterfassung", description: "Arbeitszeiten, Pausen, Anfahrt und Stunden mobil statt auf Papier oder in Excel erfassen." },
  { value: "distress", label: "Einsatz- & Objektkoordination", description: "Mobile Teams, Objekte, Schichten und Einsätze transparent koordinieren." },
  { value: "transformation", label: "Arbeitsnachweise & Digitalisierung", description: "Leistungsnachweise, Kundenfreigaben und administrative Abläufe digitalisieren." },
  { value: "none", label: "Keine Akquise", description: "Geprüft, aber kein Akquise-Kandidat." },
  { value: "blocked", label: "Gesperrt", description: "Dauerhaft sperren." }
];

export const acquisitionPriorityOptions = [
  { value: "1", label: "1 Hoch" },
  { value: "2", label: "2 Mittel" },
  { value: "3", label: "3 Niedrig" }
];

export const outreachStageOptions: Array<{ value: OutreachStage; label: string; description: string }> = [
  { value: "open", label: "Offen", description: "Noch keine aktive Akquise-Aktion." },
  { value: "acquisition_email_sent", label: "Akquisemail gesendet", description: "Erste Mail wurde versendet und in HubSpot gespiegelt." },
  { value: "called", label: "Angerufen", description: "Telefonisch kontaktiert, Ergebnis noch offen." },
  { value: "not_interested", label: "Kein Interesse", description: "Nicht weiter kontaktieren; Mail-Outreach wird gesperrt." },
  { value: "lead", label: "Lead", description: "Positives Signal, weiter im Sales-Prozess." }
];

export type LeadFilters = typeof emptyLeadFilters;
export type OutreachHandoff = {
  filters: LeadFilters;
  selectedIds: number[];
  startedAt: number;
  autoSelect?: boolean;
  workspace?: "email" | "calling";
  callBucket?: "manual" | "after_email";
  notice?: string;
};
export type ReviewHandoff = { filters: LeadFilters; selectedIds: number[]; rows?: Lead[]; startedAt: number };

export const leadQualityOptions: Array<{ value: LeadQuality; label: string }> = [
  { value: "good_fit", label: "Guter Fit" },
  { value: "review", label: "Prüfen" },
  { value: "chain", label: "Kette" },
  { value: "hotel", label: "Hotel" },
  { value: "non_company", label: "Kein Zielunternehmen" },
  { value: "unclear", label: "Unklar" }
];

export type HelpMessage = { role: "user" | "assistant"; content: string };
